<script setup lang="ts">
// Import required Vue components and utilities
import PixelCanvas from "@/features/draw/components/canvas/PixelCanvas.vue";
import { useColorManagement } from "@/features/draw/composables/useColorManagement";
import { useFileHandling } from "@/features/draw/composables/useFileHandling";
import { useUIControls } from "@/features/draw/composables/useUIControls";
import { useAvatarStore } from "@/features/draw/stores/useAvatarStore";
import type { ColorStats, Layer } from "@/features/draw/types";
import { computed, ref, watch } from "vue";
import AutoFillDialog from "./dialogs/AutoFillDialog.vue";
import MergeColorsDialog from "./dialogs/MergeColorsDialog.vue";
import NoiseRemovalDialog from "./dialogs/NoiseRemovalDialog.vue";
import ColorStatsSection from "./sections/ColorStatsSection.vue";
import LayersSection from "./sections/LayersSection.vue";
import PromptHistory from "./sections/PromptHistory.vue";
import PromptSection from "./sections/PromptSection.vue";

// Define props interface for component
const props = defineProps<{
  selectedTool: string;
  brushSize: number;
  mergedLayerPixels: Uint32Array;
  layers: Layer[];
  selectedLayerId: number;
  currentColor: string;
  selectedArea: Uint8Array;
  canvasRef: InstanceType<typeof PixelCanvas> | null;
  colorStats: ColorStats;
}>();

const isGenerating = defineModel<boolean>("isGenerating", {
  required: true,
  default: false,
});
const progress = defineModel<number>("progress", {
  required: true,
  default: 0,
});
const isHistoryVisible = defineModel<boolean>("isHistoryVisible", {
  required: true,
  default: false,
});
const latestRawImageUrl = defineModel<string>("latestRawImageUrl", {
  required: true,
  default: "",
});

// Initialize UI controls
const {
  sideButtonStyles,
  mainButtonStyles,
  sideIconSize,
  mainIconSize,
  controlButtonsStyles,
} = useUIControls();

// Define emits interface for component events
const emit = defineEmits<{
  (e: "update:tool", tool: string): void;
  (e: "update:color", color: string): void;
  (e: "update:brushSize", size: number): void;
  (e: "update:selectedLayerId", layerId: number): void;
  (e: "update:layerVisibility", update: { id: number; visible: boolean }): void;
  (e: "clear-layer", layerId: number): void;
  (e: "clear-all-layers"): void;
  (
    e: "generate-layer",
    data: { layerId: number; pixels: Uint32Array; forceAddToHistory?: boolean }
  ): void;
  (
    e: "add-content-from-all",
    data: { layerId: number; pixels: string[] }
  ): void;
  (e: "start-area-selection", layerId: number): void;
  (e: "merge-colors", layers: Layer[], forceAddToHistory: boolean): void;
  (e: "highlight-colors", colors: string[]): void;
  (e: "add-from-selection", data: { layerId: number }): void;
  (e: "import-data", layers: Layer[]): void;
  (e: "merge-colors-by-count", newColorStats: ColorStats): void;
  (e: "create-layer"): void;
  (e: "delete-layer", layerId: number): void;
  (e: "set-view-mode", mode: "pixel" | "raw"): void;
}>();

// Remove prompt-related state
const showMergeDialog = ref(false);
const showNoiseDialog = ref(false);
const showAutoFillDialog = ref(false);

const avatarStore = useAvatarStore();
// Add prompt-related state
const dynamicPrompt = ref("");
const promptStrength = ref(1);
const promptLineWeight = ref(0.5);

const promptSectionRef = ref<InstanceType<typeof PromptSection> | null>(null);
const promptHistoryRef = ref<InstanceType<typeof PromptHistory> | null>(null);

// Initialize composables
const {
  getUniqueColors,
  getColorStatistics,
  mergeColorsByThreshold,
  mergeColorsByCount,
  replaceColors,
  deleteColors,
  removeNoise,
  autoFill,
  addContentFromLayers,
} = useColorManagement();

const { handleImageUpload, handleDataImport, exportData, handleImageFile } =
  useFileHandling();

// Get unique colors from all visible layers (excluding white)
const uniqueCanvasColors = computed(() => {
  return getUniqueColors(props.layers);
});

// Check if there is an active selection
const hasSelection = computed(
  () =>
    props.selectedArea && props.selectedArea.some((isSelected) => isSelected)
);

// Calculate color statistics for visible layers
const colorStats = computed(() => {
  if (hasSelection.value) {
    // If there's a selection, only count colors within selected area
    return getColorStatistics(props.layers, props.selectedArea);
  } else {
    // Otherwise get stats for all visible layers
    return getColorStatistics(props.layers);
  }
});

// Add content from other layers to target layer based on color matching
const handleAddContent = (data: {
  layerId: number;
  colors: string[];
  tolerance: number;
  removeOriginal: boolean;
}) => {
  const result = addContentFromLayers(
    props.layers,
    data.layerId,
    data.colors,
    data.tolerance,
    data.removeOriginal
  );

  emit("generate-layer", {
    layerId: data.layerId,
    pixels: result.targetPixels,
    forceAddToHistory: true,
  });

  // Update source layers if needed
  result.updatedSourceLayers.forEach((sourceLayer) => {
    emit("generate-layer", {
      layerId: sourceLayer.id,
      pixels: sourceLayer.pixels,
      forceAddToHistory: false,
    });
  });
};

// Add content from selection to target layer
const handleAddFromSelection = (data: { layerId: number }) => {
  emit("add-from-selection", data);
};

// Merge similar colors based on color difference threshold
const handleMergeColors = (
  threshold: number,
  forceAddToHistory: boolean = true
) => {
  const updatedLayers = mergeColorsByThreshold(props.layers, threshold);
  emit("merge-colors", updatedLayers, forceAddToHistory);
  showMergeDialog.value = false;
};

// Handle merging colors by count
const handleMergeColorsByCount = (newColorStats: ColorStats) => {
  const updatedLayers = mergeColorsByCount(
    props.layers,
    props.colorStats,
    newColorStats
  );
  emit("merge-colors", updatedLayers, true);
};

// Remove noise pixels based on neighbor analysis
const handleNoiseRemoval = (threshold: number) => {
  const layer = props.layers.find((l) => l.id === props.selectedLayerId);
  if (!layer) return;

  const newPixels = removeNoise(layer, threshold, props.selectedArea);

  emit("generate-layer", {
    layerId: props.selectedLayerId,
    pixels: newPixels,
  });

  showNoiseDialog.value = false;
};

// Auto fill empty pixels based on neighbor analysis
const handleAutoFill = (threshold: number) => {
  const layer = props.layers.find((l) => l.id === props.selectedLayerId);
  if (!layer) return;

  const newPixels = autoFill(layer, threshold);

  emit("generate-layer", {
    layerId: props.selectedLayerId,
    pixels: newPixels,
  });

  showAutoFillDialog.value = false;
};

// Replace selected colors with target color
const handleReplaceColors = (data: {
  colors: string[];
  targetColor: string;
}) => {
  const updatedLayers = replaceColors(
    props.layers,
    data.colors,
    data.targetColor
  );
  emit("merge-colors", updatedLayers, true);
};

// Delete selected colors (replace with white)
const handleDeleteColors = (colors: string[]) => {
  const updatedLayers = deleteColors(props.layers, colors);
  emit("merge-colors", updatedLayers, true);
};

// Add to the script setup section
const updatePrompt = (prompt: string) => {
  if (promptSectionRef.value) {
    promptSectionRef.value.updateFromHistory({
      prompt,
      strength: promptStrength.value,
      lineWeight: promptLineWeight.value,
    });
  }
};

// Handle image upload and processing
const handleImageUploadEvent = async (event: Event) => {
  await handleImageUpload(event, {
    selectedLayerId: props.selectedLayerId,
    layers: props.layers,
    onGenerateLayer: (data) => emit("generate-layer", data),
    onMergeColors: (layers, forceAddToHistory) =>
      emit("merge-colors", layers, forceAddToHistory),
  });
};

const handleImageFileEvent = async (file: File) => {
  await handleImageFile(file, {
    selectedLayerId: props.selectedLayerId,
    layers: props.layers,
    onGenerateLayer: (data) => emit("generate-layer", data),
    onMergeColors: (layers, forceAddToHistory) =>
      emit("merge-colors", layers, forceAddToHistory),
  });
};

// Handle data import from JSON file
const handleDataImportEvent = async (event: Event) => {
  await handleDataImport(event, (layers) => emit("import-data", layers));
};

// Export canvas data to JSON file
const exportDataEvent = () => {
  exportData({
    layers: props.layers,
    getUniqueColors,
    getColorStatistics,
  });
};

// Add new state for controls visibility
const isControlsVisible = computed(() => avatarStore.isControlsVisible);

// Watch isGenerating to automatically switch to raw view mode when generation completes
watch(isGenerating, (newValue, oldValue) => {
  if (oldValue === true && newValue === false && latestRawImageUrl.value) {
    // Generation has completed and we have a raw image, switch to raw view mode
    emit("set-view-mode", "raw");
  }
});

const toggleControls = async () => {
  if (!isGenerating.value && promptSectionRef.value) {
    await promptSectionRef.value.handleGenerateAvatar();
  }
};

const showControls = () => {
  avatarStore.isControlsVisible = true;
};
const hideControls = () => {
  avatarStore.isControlsVisible = false;
};

// Expose methods to parent component for external control
defineExpose({
  // File handling
  handleImageUpload: handleImageUploadEvent,
  handleImageFile: handleImageFileEvent,
  handleDataImport: handleDataImportEvent,
  exportData: exportDataEvent,

  // Tool controls
  mergeColors: () => {
    showMergeDialog.value = true;
  },
  handleMergeColors,
  removeNoise: () => {
    showNoiseDialog.value = true;
  },
  clearCanvas: () => {
    if (confirm("确定要清除所有图层吗？")) {
      emit("clear-all-layers");
    }
  },
  autoFill: () => {
    showAutoFillDialog.value = true;
  },

  // Prompt and history
  updatePrompt,
  promptHistoryRef,
  getCurrentPrompt: () => avatarStore.positivePrompt,
  getCurrentStrength: () => promptStrength.value,
  getCurrentLineWeight: () => promptLineWeight.value,

  // Controls visibility
  toggleControls,
  isControlsVisible,
});

const showHistory = () => {
  isHistoryVisible.value = true;
};

const hideHistory = () => {
  isHistoryVisible.value = false;
};
</script>

<template>
  <div :class="controlButtonsStyles">
    <button :class="sideButtonStyles" @click="showHistory">
      <i class="iconify mdi--history text-center" :class="sideIconSize"></i>
    </button>

    <div class="flex flex-col items-center justify-center gap-2">
      <div class="flex items-center gap-1">
        5
        <PowerIcon class="relative -top-[1px]" />
      </div>
      <button
        v-show="!isGenerating"
        :class="[mainButtonStyles, isGenerating ? 'cursor-not-allowed' : '']"
        @click="toggleControls"
        :disabled="isGenerating"
        class="relative"
      >
        <i
          class="iconify text-center mdi--flash"
          :class="[mainIconSize, isGenerating ? 'mdi--loading mdi--spin' : '']"
        />
      </button>
    </div>

    <button :class="sideButtonStyles" @click="showControls">
      <i class="iconify mdi--cog text-center" :class="sideIconSize"></i>
    </button>
  </div>
  <!-- Add overlay -->
  <div
    v-if="isControlsVisible || isHistoryVisible"
    class="fixed inset-0 bg-black/20 z-40 transition-opacity duration-300"
    @click="
      hideControls();
      hideHistory();
    "
  ></div>

  <div>
    <PromptHistory
      class="fixed top-0 right-0 h-full w-[35vh] bg-base-100 shadow-lg transition-transform duration-300 z-50 min-w-[260px]"
      :class="{
        'translate-x-0': isHistoryVisible,
        'translate-x-full': !isHistoryVisible,
      }"
      ref="promptHistoryRef"
      :selected-layer="selectedLayerId"
      @generate-layer="emit('generate-layer', $event)"
      @merge-colors="handleMergeColors"
      @update-dynamic-prompt="(p: string) => dynamicPrompt = p"
      @update:strength="(v: number) => promptStrength = v"
      @update:lineWeight="(v: number) => promptLineWeight = v"
      @set-view-mode="(mode: 'pixel' | 'raw') => emit('set-view-mode', mode)"
    />
  </div>

  <div
    v-show="isControlsVisible"
    class="fixed min-w-[280px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-base-100 z-50 shadow-lg rounded-lg transition-all duration-300"
  >
    <div class="avatar-controls rounded-lg flex flex-col gap-1">
      <PromptSection
        class=""
        ref="promptSectionRef"
        :canvasRef="canvasRef"
        v-model:positiveDynamicPrompt="dynamicPrompt"
        v-model:strength="promptStrength"
        v-model:lineWeight="promptLineWeight"
        v-model:isGenerating="isGenerating"
        v-model:progress="progress"
        :selectedLayerId="selectedLayerId"
        v-model:isControlsVisible="isControlsVisible"
        v-model:latestRawImageUrl="latestRawImageUrl"
        @generate-layer="emit('generate-layer', $event)"
        @merge-colors="handleMergeColors"
      />

      <LayersSection
        class="hidden"
        :layers="layers"
        :selectedLayerId="selectedLayerId"
        :unique-canvas-colors="uniqueCanvasColors"
        :has-selection="hasSelection"
        @update:selectedLayerId="$emit('update:selectedLayerId', $event)"
        @update:color="$emit('update:color', $event)"
        @update:layer-visibility="$emit('update:layerVisibility', $event)"
        @clear-layer="$emit('clear-layer', $event)"
        @add-content="handleAddContent"
        @add-from-selection="handleAddFromSelection"
        @create-layer="$emit('create-layer')"
        @delete-layer="$emit('delete-layer', $event)"
      />
      <NoiseRemovalDialog
        class="hidden"
        v-if="showNoiseDialog"
        @remove-noise="handleNoiseRemoval"
        @close="showNoiseDialog = false"
      />

      <AutoFillDialog
        v-if="showAutoFillDialog"
        @auto-fill="handleAutoFill"
        @close="showAutoFillDialog = false"
      />

      <MergeColorsDialog
        v-if="showMergeDialog"
        @merge="handleMergeColors"
        @close="showMergeDialog = false"
      />
    </div>

    <ColorStatsSection
      v-if="false"
      class="hidden"
      :color-stats="colorStats"
      :current-color="currentColor"
      :has-selection="hasSelection"
      @update:color="$emit('update:color', $event)"
      @highlight-colors="$emit('highlight-colors', $event)"
      @replace-colors="handleReplaceColors"
      @delete-colors="handleDeleteColors"
      @merge-colors-by-count="handleMergeColorsByCount"
    />
  </div>
</template>
